/**
 * Cookie consent store - manages cookie consent state with persistence
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface CookiePreferences {
  essential: boolean; // Always true, required for app to function
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentState {
  // Whether user has made a choice (accepted or customized)
  hasConsented: boolean;

  // User's cookie preferences
  preferences: CookiePreferences;

  // Actions
  acceptAll: () => void;
  acceptEssentialOnly: () => void;
  savePreferences: (preferences: Partial<CookiePreferences>) => void;
  resetConsent: () => void;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
};

export const useCookieConsentStore = create<CookieConsentState>()(
  persist(
    (set) => ({
      hasConsented: false,
      preferences: DEFAULT_PREFERENCES,

      acceptAll: () =>
        set({
          hasConsented: true,
          preferences: {
            essential: true,
            analytics: true,
            marketing: false,
          },
        }),

      acceptEssentialOnly: () =>
        set({
          hasConsented: true,
          preferences: {
            essential: true,
            analytics: false,
            marketing: false,
          },
        }),

      savePreferences: (newPreferences) =>
        set((state) => ({
          hasConsented: true,
          preferences: {
            ...state.preferences,
            ...newPreferences,
            essential: true, // Essential cookies are always required
          },
        })),

      resetConsent: () =>
        set({
          hasConsented: false,
          preferences: DEFAULT_PREFERENCES,
        }),
    }),
    {
      name: 'neokanban-cookie-consent',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
